import { useEffect, useState } from "react";
import { Building2, FileText, Gamepad2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = { empresa: "Site para Empresa", games: "Site de Jogo / eSports", outro: "Outro projeto" };
const TYPE_ICON = { empresa: Building2, games: Gamepad2, outro: FileText };

const STATUS_FLOW = ["novo", "em contato", "fechado"];
const STATUS_STYLE = {
  novo: "border-[#E50914]/40 bg-[#E50914]/10 text-[#FF2A3B]",
  "em contato": "border-amber-500/40 bg-amber-500/10 text-amber-400",
  fechado: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
};

const formatDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState(null);

  const load = () =>
    fetch(`${API}/admin/quotes`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setQuotes(d.quotes || []))
      .catch(() => setQuotes([]));

  useEffect(() => {
    load();
  }, []);

  const advanceStatus = async (quote) => {
    const next = STATUS_FLOW[STATUS_FLOW.indexOf(quote.status) + 1];
    if (!next) return;
    setQuotes((prev) => prev.map((q) => (q.quote_id === quote.quote_id ? { ...q, status: next } : q)));
    await fetch(`${API}/admin/quotes/${quote.quote_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status: next }),
    }).catch(() => load());
  };

  return (
    <div className="mt-10 rounded-2xl border border-[#22222A] bg-[#101014]">
      <div className="flex items-center gap-3 border-b border-[#1A1A20] px-6 py-4">
        <Inbox className="h-5 w-5 text-[#E50914]" />
        <div>
          <h2 className="font-display text-lg font-bold text-zinc-100">Pedidos de orçamento</h2>
          <p className="text-xs text-zinc-500">Solicitações feitas por clientes logados</p>
        </div>
      </div>
      <div className="space-y-4 p-4 sm:p-6" data-testid="admin-quotes-list">
        {quotes === null ? (
          <p className="py-8 text-center text-sm text-zinc-600">Carregando...</p>
        ) : quotes.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-600">Nenhum pedido de orçamento ainda.</p>
        ) : (
          quotes.map((q) => {
            const Icon = TYPE_ICON[q.project_type] || FileText;
            const next = STATUS_FLOW[STATUS_FLOW.indexOf(q.status) + 1];
            return (
              <div
                key={q.quote_id}
                data-testid={`admin-quote-${q.quote_id.slice(-6)}`}
                className="rounded-xl border border-[#1A1A20] bg-[#0A0A0C] p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E50914]/10">
                      <Icon className="h-5 w-5 text-[#E50914]" />
                    </div>
                    <div>
                      <div className="font-display text-sm font-bold text-zinc-100">{q.name}</div>
                      <div className="font-mono-code text-[11px] text-zinc-500">{q.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest ${STATUS_STYLE[q.status]}`}>
                      {q.status}
                    </span>
                    {next && (
                      <button
                        data-testid={`admin-quote-advance-${q.quote_id.slice(-6)}`}
                        onClick={() => advanceStatus(q)}
                        className="rounded-full border border-[#2A2A33] px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-zinc-400 transition-colors hover:border-[#E50914]/60 hover:text-white"
                      >
                        Marcar: {next}
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{q.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-3 font-mono-code text-[10px] text-zinc-600">
                  <span className="rounded-md bg-[#101014] px-2.5 py-1 text-zinc-400">{TYPE_LABEL[q.project_type]}</span>
                  <span>{formatDate(q.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
